import { useCheckPaymentConfigCompatibility } from "@/components/Inputs/RatesPicker/hooks/useCheckPaymentConfigCompatibility.ts";
import { calculateNights } from "@/helpers/calculateNights.ts";
import { useInitialSortedRates } from "@/helpers/hooks/useInitialSortedRates.ts";
import { RatePlanMerged } from "@/helpers/interfaces/RatePlanMerged.ts";
import { mergeRatePlans } from "@/helpers/mergeRatePlans.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { CartAccommodationItem } from "@/stores/EngineStore/interfaces/CartAccommodationItem.ts";
import { useMemo, useState } from "react";

export const useRatePlanSwitcher = (item: CartAccommodationItem) => {
  const { setCartItemRatePlan, cartItemCount } = useEngineStore((s) => ({
    setCartItemRatePlan: s.setCartItemRatePlan,
    cartItemCount: s.cartAccommodationItems.length,
  }));
  const paymentCompatibilityMap = useCheckPaymentConfigCompatibility(item.rateGroup);
  const enforcePaymentRestriction = cartItemCount >= 2;
  const [isExpanded, setIsExpanded] = useState(false);
  const [displayFirstId, setDisplayFirstId] = useState(item.ratePlan.id);

  const reservationNights = useMemo(() => {
    if (!item.dateRange[0] || !item.dateRange[1]) return 0;
    return calculateNights(item.dateRange[0], item.dateRange[1]);
  }, [item.dateRange]);

  const ratePlans = useMemo(() => mergeRatePlans(item.rateGroup), [item.rateGroup]);
  const availableRatePlans = useMemo(() => ratePlans.filter((rp) => rp.isAvailable), [ratePlans]);

  const sortedRates = useInitialSortedRates(availableRatePlans, item.ratePlan.id);

  const hasMore = sortedRates.length > 1;
  const displayFirst = sortedRates.find((rp) => rp.id === displayFirstId);
  const firstRate = displayFirst ?? sortedRates[0];
  const restRates = sortedRates.filter((rp) => rp.id !== firstRate.id);

  const handleSelectRate = (rp: RatePlanMerged) => {
    if (rp.id === item.ratePlan.id) return;
    setCartItemRatePlan(rp, item.id);
  };

  const handleToggleOtherRates = () => {
    setIsExpanded((v) => {
      if (v) setDisplayFirstId(item.ratePlan.id);
      return !v;
    });
  };

  const isPaymentIncompatible = (rp: RatePlanMerged) =>
    enforcePaymentRestriction && rp.id !== item.ratePlan.id && paymentCompatibilityMap[rp.id] === false;

  return {
    sortedRates,
    firstRate,
    restRates,
    hasMore,
    isExpanded,
    reservationNights,
    selectedRateId: item.ratePlan.id,
    handleSelectRate,
    handleToggleOtherRates,
    isPaymentIncompatible,
  };
};
